import { Loader2, Trash2, RotateCcw, Eye, ListFilter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useDeleteJob, useRetryJob } from '@/hooks/useGenerationJobs';
import StatusBadge from './StatusBadge';
import type { GenerationJob } from '@/types/generation';

interface JobListItemProps {
  job: GenerationJob;
  onViewDetails: (jobId: string) => void;
}

function formatDevelopmentAreas(da: string[] | { type: string; value: string }): string {
  if (Array.isArray(da)) {
    return da.join(', ');
  }
  return da.value;
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export default function JobListItem({ job, onViewDetails }: JobListItemProps) {
  const retryMutation = useRetryJob();
  const deleteMutation = useDeleteJob();

  const handleDelete = () => {
    if (
      window.confirm(
        'Are you sure you want to delete this generation job? This cannot be undone.'
      )
    ) {
      deleteMutation.mutate(job.id);
    }
  };

  return (
    <Card className="bg-admin-dark-800 border-admin-slate-300/10 hover:border-admin-slate-300/20 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Job info */}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={job.status} />
              <span className="text-admin-slate-500 text-xs">
                {relativeTime(job.createdAt)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-admin-slate-400 font-medium">
                {job.transition}
              </span>
              <span className="text-admin-slate-600">·</span>
              <span className="text-admin-slate-500">{job.ageGroup} years</span>
              <span className="text-admin-slate-600">·</span>
              <span className="text-admin-slate-500">
                {job.requestedCount} requested
              </span>
            </div>

            {/* Development areas / category */}
            {job.developmentAreas && (
              <div className="flex flex-wrap gap-1">
                <span
                  className="px-2 py-0.5 rounded-full text-xs bg-admin-dark-700 text-admin-slate-500 border border-admin-slate-300/10"
                >
                  {formatDevelopmentAreas(job.developmentAreas)}
                </span>
              </div>
            )}

            {/* Results if completed */}
            {job.status === 'COMPLETED' && (
              <div className="flex items-center gap-3 text-xs text-admin-slate-500">
                <span>
                  <span className="text-emerald-500 font-medium">
                    {job.validCount ?? 0}
                  </span>{' '}
                  valid
                </span>
                <span>
                  <span className="text-red-400 font-medium">
                    {job.invalidCount ?? 0}
                  </span>{' '}
                  invalid
                </span>
                <span>
                  <span className="text-admin-slate-400 font-medium">
                    {job.savedActivityIds?.length ?? 0}
                  </span>{' '}
                  saved
                </span>
              </div>
            )}

            {/* Error message if failed */}
            {job.status === 'FAILED' && job.errorMessage && (
              <p className="text-xs text-red-400 line-clamp-2">{job.errorMessage}</p>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewDetails(job.id)}
              className="text-admin-slate-500 hover:text-admin-slate-400"
              title="View details"
            >
              <Eye className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline text-xs">View</span>
            </Button>

            {job.status === 'COMPLETED' && (job.savedActivityIds?.length ?? 0) > 0 && (
              <Link to={`/admin/activities?generationJobId=${job.id}`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-admin-slate-500 hover:text-admin-accent-blue"
                  title={`View ${job.savedActivityIds?.length} generated activities`}
                >
                  <ListFilter className="w-4 h-4" />
                  <span className="ml-1 hidden sm:inline text-xs">Activities</span>
                </Button>
              </Link>
            )}

            {job.status === 'FAILED' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => retryMutation.mutate(job.id)}
                disabled={retryMutation.isPending}
                className="text-amber-500 hover:text-amber-400"
                title="Retry job"
              >
                {retryMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                <span className="ml-1 hidden sm:inline text-xs">Retry</span>
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="text-admin-slate-500 hover:text-red-400"
              title="Delete job"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
